import { VEHICLE_CLASS_LABELS, type VehicleClass } from "@shared/modules/x-atelier";

// Six silhouettes, une par classe tarifaire, dessinées dans la même boîte
// (40 × 20) pour s'aligner dans une pastille de prix. Le remplissage prend la
// couleur du texte : la même icône sert dans une pastille claire, une pastille
// choisie (texte inversé) ou un bouton. Les formes se distinguent par la
// hauteur du toit et la présence d'une caisse arrière : un coupé bas et
// fuyant, une berline à trois volumes, un VUS haut, un grand VUS plus long,
// une camionnette avec sa boîte, un gros camion plus massif.

interface Silhouette {
  body: string;
  /** Abscisses des deux roues. */
  wheels: [number, number];
}

const SILHOUETTES: Record<VehicleClass, Silhouette> = {
  coupe: {
    body: "M3 15v-3.4c0-1.3.9-2.1 2.2-2.3l6-.9 4.6-4.3C16.7 3.3 17.8 3 19.2 3h5.6c1.7 0 3.1.7 4.4 2l3.1 3.4 4.1.8c1.4.3 2.3 1.3 2.3 2.7V15z",
    wheels: [10, 30],
  },
  sedan: {
    body: "M3 15v-3.2c0-1.4.9-2.3 2.4-2.6l4-.7 3.8-4.5C14 3.1 15 2.6 16.4 2.6H25c1.5 0 2.6.6 3.6 1.8l3.4 4.1 3.8.7c1.5.3 2.2 1.2 2.2 2.6V15z",
    wheels: [10, 30],
  },
  suv: {
    body: "M3 15V7.5C3 5.6 4.3 4.5 6.2 4.5h15.6c1.7 0 2.8.6 3.8 1.8l3.6 4.4 4.4.8c1.5.3 2.4 1.3 2.4 2.7V15z",
    wheels: [10, 30],
  },
  large_suv: {
    body: "M2 15V6.8c0-2 1.3-3.2 3.3-3.2h18.4c1.8 0 3 .7 4 2l3.4 4.6 4.6.8c1.5.3 2.3 1.3 2.3 2.7V15z",
    wheels: [9.5, 31],
  },
  truck: {
    body: "M2 15V9c0-.8.6-1.4 1.4-1.4H15V5.8c0-1.4 1-2.3 2.4-2.3h6.8c1.7 0 2.9.7 3.9 2l3.2 4.3 4.2.8c1.5.3 2.5 1.3 2.5 2.7V15z",
    wheels: [9, 31],
  },
  large_truck: {
    body: "M1 15V8.2c0-.9.6-1.5 1.5-1.5H14V4.6c0-1.5 1-2.6 2.6-2.6h8c1.9 0 3.2.8 4.3 2.2l3.4 4.8 4.4.9c1.6.3 2.3 1.4 2.3 2.8V15z",
    wheels: [8, 32],
  },
};

/** Silhouette d'une classe de véhicule, à la couleur du texte courant. */
export function VehicleIcon({
  vehicleClass,
  width = 32,
  className,
}: {
  vehicleClass: VehicleClass;
  width?: number;
  className?: string;
}) {
  const { body, wheels } = SILHOUETTES[vehicleClass];
  return (
    <svg
      className={className}
      width={width}
      height={width / 2}
      viewBox="0 0 40 20"
      role="img"
      aria-label={VEHICLE_CLASS_LABELS[vehicleClass]}
    >
      <title>{VEHICLE_CLASS_LABELS[vehicleClass]}</title>
      <path d={body} fill="currentColor" />
      {wheels.map((x) => (
        <g key={x}>
          <circle cx={x} cy={15.5} r={3} fill="currentColor" />
          <circle cx={x} cy={15.5} r={1.3} fill="#fff" fillOpacity={0.9} />
        </g>
      ))}
    </svg>
  );
}
